import { getApi, patchApi, postApi } from "../helpers/auth_helpers";
import type {
  ApiResult,
  CashDay,
  CashEntryDetail,
  CashInbox,
  ClearedMissing,
  ClearMissingRequest,
  CashStatus,
  ChangeList,
  ChangeRequest,
  CheckedHistory,
  CorrectRequest,
  CreateTransferRequest,
  DecideRequest,
  DecideResponse,
  EnterRowsRequest,
  EnterRowsResponse,
  ExpenseRequest,
  ExpenseResponse,
  HolderList,
  LedgerHead,
  LedgerHeadList,
  LedgerHeadRequest,
  PhotoReadResponse,
  PrintCheck,
  PrintList,
  PrintRequest,
  PrintView,
  RecordType,
  RejectTransferRequest,
  ReviseTransferRequest,
  ReviseTransferResponse,
  SavePhotoRequest,
  SavePhotoResponse,
  SaveTotalRequest,
  SpendsCheck,
  SpendsTotalRequest,
  TransferDetailResponse,
  TransferView,
  TransferWithCash,
  UploadUrlRequest,
  UploadUrlResponse,
  VerifyRequest,
  VerifyResponse,
  WorkerPayments,
} from "../pages/karkhana/cashbook/types";

/**
 * Cash book v1 API (V1_SPEC section 8). Every call resolves to the server envelope
 * `{ success, status, message, data, code? }`; a network failure rejects.
 */
const base = () => `${process.env.REACT_APP_SERVERURL || ""}/karkhana`;
const cb = () => `${base()}/cash-book`;
const enc = encodeURIComponent;

type R<T> = Promise<ApiResult<T>>;

const qs = (q: Record<string, string | number | boolean | null | undefined>) => {
  const s = Object.entries(q)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${enc(k)}=${enc(String(v))}`)
    .join("&");
  return s ? `?${s}` : "";
};

// ---- 8.1 status, prints, photos, check ------------------------------------------------------------

export const getCashbookStatus = (): R<CashStatus> => getApi(`${cb()}/status`);

export const printPaySheet = (paySheetId: string, body: PrintRequest): R<PrintView> =>
  postApi(`${base()}/pay-sheets/${enc(paySheetId)}/print`, body);

export const listPaySheetPrints = (paySheetId: string): R<PrintList> => getApi(`${base()}/pay-sheets/${enc(paySheetId)}/prints`);

export const listLivePrints = (days = 14): R<PrintList> => getApi(`${cb()}/prints${qs({ live: true, days })}`);

export const getPrint = (printId: string): R<PrintView> => getApi(`${cb()}/prints/${enc(printId)}`);

export const getCashPhotoUploadUrl = (body: UploadUrlRequest): R<UploadUrlResponse> => postApi(`${cb()}/photos/upload-url`, body);

/** PUT the photo bytes to the signed private-bucket address. Throws when storage refuses it. */
export const putCashPhoto = async (uploadUrl: string, blob: Blob, contentType: string): Promise<void> => {
  const res = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": contentType }, body: blob });
  if (!res.ok) throw new Error("KEY_INVALID");
};

export const saveCashPhoto = (body: SavePhotoRequest): R<SavePhotoResponse> => postApi(`${cb()}/photos`, body);

export const getCashPhoto = (photoId: string): R<PhotoReadResponse> => getApi(`${cb()}/photos/${enc(photoId)}`);

export const getPrintCheck = (printId: string): R<PrintCheck> => getApi(`${cb()}/prints/${enc(printId)}/check`);

export const savePrintTotal = (printId: string, body: SaveTotalRequest): R<PrintCheck> => postApi(`${cb()}/prints/${enc(printId)}/total`, body);

export const enterPrintRows = (printId: string, body: EnterRowsRequest): R<EnterRowsResponse> =>
  postApi(`${cb()}/prints/${enc(printId)}/rows`, body);

export const verifyEntries = (body: VerifyRequest): R<VerifyResponse> => postApi(`${cb()}/verify`, body);

// ---- 8.1 entries and change requests --------------------------------------------------------------

export const getCashEntry = (type: RecordType, id: string): R<CashEntryDetail> => getApi(`${cb()}/entries/${type}/${enc(id)}`);

export const correctCashEntry = (type: RecordType, id: string, body: CorrectRequest): R<CashEntryDetail> =>
  postApi(`${cb()}/entries/${type}/${enc(id)}/correct`, body);

export const requestCashChange = (type: RecordType, id: string, body: ChangeRequest): R<CashEntryDetail> =>
  postApi(`${cb()}/entries/${type}/${enc(id)}/changes`, body);

export const listCashChanges = (state = "PENDING"): R<ChangeList> => getApi(`${cb()}/changes${qs({ state })}`);

export const approveCashChange = (proposalSeq: number, body: DecideRequest): R<DecideResponse> =>
  postApi(`${cb()}/changes/${proposalSeq}/approve`, body);

export const rejectCashChange = (proposalSeq: number, body: DecideRequest): R<DecideResponse> =>
  postApi(`${cb()}/changes/${proposalSeq}/reject`, body);

// ---- 8.2 day, holders, heads, spends --------------------------------------------------------------

export const getCashDay = (date?: string, holder?: string): R<CashDay> => getApi(`${cb()}/day${qs({ date, holder })}`);

export const listCashHolders = (): R<HolderList> => getApi(`${cb()}/holders`);

export const listLedgerHeads = (includeRetired = false): R<LedgerHeadList> => getApi(`${cb()}/ledger-heads${qs({ includeRetired })}`);

export const createLedgerHead = (body: LedgerHeadRequest): R<{ head: LedgerHead }> => postApi(`${cb()}/ledger-heads`, body);

export const updateLedgerHead = (id: string, body: LedgerHeadRequest): R<{ head: LedgerHead }> =>
  patchApi(`${cb()}/ledger-heads/${enc(id)}`, body);

export const saveExpense = (body: ExpenseRequest): R<ExpenseResponse> => postApi(`${cb()}/expenses`, body);

export const getSpendsCheck = (photoId: string): R<SpendsCheck> => getApi(`${cb()}/spends/${enc(photoId)}/check`);

export const saveSpendsTotal = (photoId: string, body: SpendsTotalRequest): R<SpendsCheck> =>
  postApi(`${cb()}/spends/${enc(photoId)}/total`, body);

// ---- 8.2 transfers --------------------------------------------------------------------------------

export const createTransfer = (body: CreateTransferRequest): R<TransferWithCash> => postApi(`${cb()}/transfers`, body);

export const getTransfer = (id: string): R<TransferDetailResponse> => getApi(`${cb()}/transfers/${enc(id)}`);

export const confirmTransfer = (id: string, body: { version: number }): R<TransferWithCash> =>
  postApi(`${cb()}/transfers/${enc(id)}/confirm`, body);

export const rejectTransfer = (id: string, body: RejectTransferRequest): R<TransferWithCash> =>
  postApi(`${cb()}/transfers/${enc(id)}/reject`, body);

export const reviseTransfer = (id: string, body: ReviseTransferRequest): R<ReviseTransferResponse> =>
  postApi(`${cb()}/transfers/${enc(id)}/revise`, body);

export const disputeTransfer = (id: string, body: { note: string }): R<{ transfer: TransferView }> =>
  postApi(`${cb()}/transfers/${enc(id)}/dispute`, body);

export const closeTransferDispute = (id: string, body: { note: string }): R<{ transfer: TransferView }> =>
  postApi(`${cb()}/transfers/${enc(id)}/close-dispute`, body);

// ---- 8.2 owner lists ------------------------------------------------------------------------------

export const getCashInbox = (days = 14): R<CashInbox> => getApi(`${cb()}/inbox${qs({ days })}`);

export const clearMissing = (body: ClearMissingRequest): R<ClearedMissing> => postApi(`${cb()}/missing/clear`, body);

export const getCheckedHistory = (before?: string | null, limit = 20): R<CheckedHistory> => getApi(`${cb()}/history${qs({ before, limit })}`);

export const getWorkerPayments = (personId: string, before?: string | null): R<WorkerPayments> =>
  getApi(`${cb()}/workers/${enc(personId)}/payments${qs({ before })}`);
